import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';

import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { selectors as cartSelectors } from '@elab/core/store/cart';
import { selectors as configSelectors } from '@elab/core/store/config';
import { featureNames } from '@elab/core/utils/constants';

import { actions } from './redux';

/**
 * Component is used to add gift customization for product on the cart page
 *
 * @attr {string} quantity-label - Quantity title text (info section)
 * @attr {string} total-order-label - total order text (info section for top item)
 * @attr {string} all-pack-label - text for title all pack section
 * @attr {string} each-pack-label - text for title each pack section
 * @attr {string} unavailable-label - text for unavailable options
 *
 * @param {array} entries - The entries in the cart
 * @param {array} cartGiftOptions - gift options for the cart lvl
 * @param {array} orderInfos - gift options for the cart
 * @param {string} giftWrapCode - gift option code that shouldn't be shown
 */

class GiftCustomizationList extends SimpleLitElement {
    static get properties() {
        return {
            entries: { type: Array, attribute: false },
            cartGiftOptions: { type: Array, attribute: false },
            orderInfos: { type: Array, attribute: false },
            supportedFeatures: { type: Array, attribute: false },
            quantityLabel: { type: String, attribute: 'quantity-label' },
            hideGiftOptions: { type: Boolean, attribute: 'hide-gift-options' },
            totalOrderLabel: { type: String, attribute: 'total-order-label' },
            allPackLabel: { type: String, attribute: 'all-pack-label' },
            eachPackLabel: { type: String, attribute: 'each-pack-label' },
            unavailableLabel: { type: String, attribute: 'unavailable-label' },
            freeLabel: { type: String, attribute: 'free-label' },
            analyticsConfig: { type: String, attribute: 'analytics-config' },
            gwpInfoText: { type: String, attribute: 'gwp-info-text' },
            giftWrapCode: { type: String, attribute: false },
            subTitle: { type: String, attribute: 'sub-title' },
            giftOptionAlert: { type: String, attribute: 'gift-option-alert-label' },
            giftOptionMessageMandatory: {
                type: Boolean,
                attribute: 'gift-option-message-mandatory',
            },
        };
    }

    constructor() {
        super();
        this.entries = [];
        this.cartGiftOptions = [];
        this.orderInfos = [];
        this.quantityLabel = '';
        this.totalOrderLabel = '';
        this.allPackLabel = '';
        this.eachPackLabel = '';
        this.unavailableLabel = '';
        this.freeLabel = '';
        this.analyticsConfig = '';
        this.subTitle = '';
        this.hideGiftOptions = false;
        this.giftOptionAlert = '';
        this.giftOptionMessageMandatory = false;
    }

    getGiftClasses = item => {
        return {
            'gifts-customization__item': true,
            'gifts-customization__item--top': item.isCartOption,
            'gifts-customization__item--freeSample': item.freeSample,
        };
    };

    renderItem = item => {
        return item.isGwp
            ? html``
            : html`
                  <div class="${classMap(this.getGiftClasses(item))}">
                      <div class="gifts-customization__wrapper">
                          ${this.renderItemInfo(item)} ${this.renderItemPrice(item)}
                      </div>

                      <div class="gifts-customization__item-options">
                          ${this.renderItemHandler(item)}
                      </div>
                      <div class="gifts-customization__item-alert">
                          ${this.renderAlertMessage(item)}
                      </div>
                  </div>
              `;
    };

    renderAlertMessage({ isGwp, topItem }) {
        return isGwp || topItem || !this.giftOptionAlert.trim()
            ? html``
            : this.hasGiftOptions
            ? html`
                  <e2-message type="info">
                      <div v-slot="message">${this.giftOptionAlert}</div>
                  </e2-message>
              `
            : html``;
    }

    renderGwpOption() {
        return html`
            <div class="gifts-customization__item-options--gwp">
                <i class="icon icon-error-msg"></i>
                <p class="gifts-customization__gwp-text">
                    ${this.gwpInfoText}
                </p>
            </div>
        `;
    }

    renderItemHandler(item) {
        return item.isGwp ? this.renderGwpOption() : this.renderItemOptions(item);
    }

    renderItemInfo({ product = {}, quantity, topItem }) {
        return html`
            <div class="gifts-customization__item-info">
                ${topItem ? this.renderTopItem() : this.renderAllInfo(product, quantity)}
            </div>
        `;
    }

    renderTopItem() {
        return html`
            <div class="gifts-customization__item-range">
                ${this.totalOrderLabel}
            </div>
        `;
    }

    renderAllInfo(product, quantity) {
        const { masterBrand, name, rangeName, code, variantValueCategories = [] } = product;

        return html`
            <div class="gifts-customization__item-brand">
                ${masterBrand && masterBrand.name}
            </div>
            <div class="gifts-customization__item-range">
                ${rangeName}
            </div>
            <div>${name}</div>
            <e2-product-variants
                .variants="${variantValueCategories}"
                .productCode="${code}"
                variant-class="gifts-customization__item-variant"
            ></e2-product-variants>
            ${quantity
                ? html`
                      <div>${this.quantityLabel} ${quantity}</div>
                  `
                : null}
        `;
    }

    renderItemPrice({ totalPrice = {}, totalPriceWithoutPromotions = {} }) {
        return html`
            <div class="gifts-customization__item-price">
                <div class="added-product__price-old smaller">
                    ${totalPriceWithoutPromotions.formattedValue != totalPrice.formattedValue
                        ? totalPriceWithoutPromotions.formattedValue
                        : ''}
                </div>
                <e2-price-badge price="${totalPrice.formattedValue}"></e2-price-badge>
            </div>
        `;
    }

    renderClickTrackingWrapper(item, configurationInfos, product, isCartOption, giftOptions) {
        return html`
            <e2-click-tracking
                analytics-config="${this.analyticsConfig}"
                type="${item.price.value > 0 ? 'Gift-Message' : 'Gift-Wrap'}"
                class="gifts-customization"
            >
                ${this.renderOption(item, configurationInfos, product, isCartOption, giftOptions)}
            </e2-click-tracking>
        `;
    }

    renderItemOptions({ giftOptions, configurationInfos, isCartOption, product }) {
        let filtered = this.featureEnabled(featureNames.giftOptions)
            ? giftOptions
            : giftOptions.filter(e => e.code === this.giftWrapCode);
        filtered = this.featureEnabled(featureNames.giftMessage)
            ? filtered
            : filtered.filter(e => e.code !== this.giftWrapCode);
        return html`
            ${this.hasGiftOptions
                ? filtered.map(item => {
                      return this.renderClickTrackingWrapper(
                          item,
                          configurationInfos,
                          product,
                          isCartOption,
                          filtered,
                      );
                  })
                : this.renderOptionUnavailable()}
        `;
    }

    renderOption = (
        { price, name, code, giftOptionMessageEnabled, giftOptionMessageMandatory },
        configurationInfos = [],
        product = {},
        isCartOption,
        giftOptions,
    ) => {
        const isApplied = this.isAppliedOption(isCartOption, code, configurationInfos);
        // Check to see if specific error logic need to be placed
        if (giftOptionMessageMandatory) {
            this.dispatchEvent(new CustomEvent('set-wrap-error', { detail: true }));
        }
        return html`
            <a
                class="gifts-customization__item-option ${classMap({
                    'gifts-customization__item-selected': isApplied,
                })}"
                @click="${e =>
                    this.onSelectOption(
                        e,
                        code,
                        product.code,
                        isCartOption,
                        giftOptionMessageEnabled,
                        isApplied,
                        giftOptionMessageMandatory,
                        giftOptions,
                    )}"
            >
                <div class="gifts-customization__options-name">
                    ${name}
                </div>
                ${price.value
                    ? html`
                          <span class="gifts-customization__options-price">
                              <e2-price-badge
                                  price-label="+"
                                  price="${price.formattedValue}"
                                  additional-class="gifts-customization"
                              ></e2-price-badge>
                          </span>
                      `
                    : html`
                          <span class="gifts-customization__options-free">
                              ${this.freeLabel}
                          </span>
                      `}
            </a>
        `;
    };

    get hasGiftOptions() {
        return this.featureEnabled(featureNames.giftOptions);
    }

    featureEnabled(featureName) {
        return !!this.supportedFeatures.find(feature => feature.code === featureName);
    }

    onSelectOption = (
        e,
        code,
        productCode,
        isCartOption,
        giftOptionMessageEnabled,
        isApplied,
        giftOptionMessageMandatory,
        giftOptions,
    ) => {
        e.preventDefault();

        this.dispatchEvent(
            new CustomEvent('set-gift-data', {
                detail: {
                    isCartOption,
                    giftOptionCode: code,
                    code: productCode,
                    giftOptionMessageEnabled,
                    isApplied,
                    giftOptionMessageMandatory: this.giftOptionMessageMandatory
                        ? giftOptionMessageMandatory
                        : false,
                    giftOptions,
                },
            }),
        );
    };

    isAppliedOption = (isCartOption, code, configurationInfos) =>
        isCartOption
            ? this.orderInfos.some(item => item.configurationValue === code)
            : configurationInfos.some(item => item.configurationValue === code);

    renderOptionUnavailable() {
        return html`
            <div class="gifts-customization__item-options--unavailable">
                <i class="icon icon-info"></i>
                ${this.unavailableLabel}
            </div>
        `;
    }

    renderTopContainer() {
        const { cartGiftOptions, cartTotalPrice, allPackLabel, subTitle } = this;
        return html`
            <h4 class="gifts-customization__title">${allPackLabel}</h4>
            <h3 class="gifts-customization__title gifts-customization__title-description">
                ${subTitle}
            </h3>
            ${this.renderItem({
                giftOptions: cartGiftOptions,
                totalPrice: cartTotalPrice,
                isCartOption: true,
                topItem: true,
            })}
        `;
    }

    render() {
        const { entries, eachPackLabel } = this;

        return html`
            ${this.hideGiftOptions && this.cartGiftOptions && this.cartGiftOptions.length > 0
                ? this.renderTopContainer()
                : ''}
            <h4 class="gifts-customization__title">${eachPackLabel}</h4>
            ${entries.map(this.renderItem)}
        `;
    }
}

const mapStateToProps = state => ({
    entries: cartSelectors.getEntries(state),
    cartGiftOptions: cartSelectors.getCartGiftOptions(state),
    cartTotalPrice: cartSelectors.getTotalPrice(state),
    orderInfos: cartSelectors.getOrderInfos(state),
    isDeliveryModeExpress: cartSelectors.isDeliveryModeExpress(state),
    supportedFeatures: cartSelectors.getSupportedFeatures(state),
    giftWrapCode: configSelectors.getGiftWrapConfig(state).giftWrapCode || '',
});

const mapDispatchToEvents = dispatch => ({
    'set-gift-data': ({ detail }) => dispatch(actions.setGiftData(detail)),
    'set-wrap-error': ({ detail }) => dispatch(actions.updateEmptyMsgFeatureFlag(detail)),
});

export default () =>
    customElements.define(
        'e2-gift-customization-list',
        connect(mapStateToProps, mapDispatchToEvents)(GiftCustomizationList),
    );
